import classNames from 'classnames';
import React, { cloneElement, useEffect, useMemo, useRef, useState } from 'react';

import PopoverPlacement from '@ui5/webcomponents/dist/types/PopoverPlacement';
import type { PopoverDomRef } from '@ui5/webcomponents-react';
import { Popover } from '@ui5/webcomponents-react';

import styles from './Tooltip.module.css';

export interface TooltipProps {
  /**
   * Additional Text to be displayed at the bottom of the tooltip. E.g keyboard shortcuts.
   */
  additionalText?: string;
  /**
   * The element to be used as an anchor of the tooltip.
   * It must accept a `ref` for the HTMLElement, which will be used for positioning the tooltip.
   */
  children: JSX.Element;
  placement?: PopoverPlacement;
  /**
   * The content to be shown on the tooltip.
   * It can be plain text or a React component.
   * When using plain text, it will be split by double line breaks and rendered as paragraphs.
   * The value null can be used to (temporarily) disable the tooltip.
   */
  tooltipText: React.ReactNode | null;
  /**
   * Whether the tooltip is currently visible.
   * The special value 'hover' means that the tooltip is automatically shown when the user hovers over the anchor element.
   */
  visible: boolean | 'hover';
}

/**
 * A tooltip implemented using UI5 Popover component.
 * Allows interaction with tooltip content when hovering over it.
 */
export function Tooltip({
  additionalText,
  visible,
  tooltipText,
  children,
  placement = PopoverPlacement.Top,
}: TooltipProps) {
  const anchorRef = useRef<HTMLElement>(null);
  const popoverRef = useRef<PopoverDomRef>(null);
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);

  // Clone the child element and add our ref to it
  const anchor = cloneElement(children, { ref: anchorRef });

  // Determine if tooltip should be shown
  const shouldShow =
    tooltipText !== null && (visible === true || (visible === 'hover' && isVisible));

  // Effect for document click handling and keyboard events (ESC)
  useEffect(() => {
    if (tooltipText === null) {
      return;
    }

    const handleDocumentClick = (e: MouseEvent) => {
      if (!isVisible) {
        return;
      }

      // Check if click was outside both anchor and tooltip
      const clickTarget = e.target as Node;
      const anchorElement = anchorRef.current;
      const popoverElement = popoverRef.current;

      const isOutsideAnchor = anchorElement && !anchorElement.contains(clickTarget);
      const isOutsideTooltip = popoverElement && !popoverElement.contains(clickTarget);

      if (isOutsideAnchor && isOutsideTooltip) {
        if (hideTimeoutRef.current !== null) {
          window.clearTimeout(hideTimeoutRef.current);

          hideTimeoutRef.current = null;
        }

        setIsVisible(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVisible) {
        if (hideTimeoutRef.current !== null) {
          window.clearTimeout(hideTimeoutRef.current);

          hideTimeoutRef.current = null;
        }

        setIsVisible(false);
      }
    };

    if (visible === 'hover') {
      document.addEventListener('mousedown', handleDocumentClick);
    }

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      if (visible === 'hover') {
        document.removeEventListener('mousedown', handleDocumentClick);
      }

      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [visible, tooltipText, isVisible]);

  // Main effect to handle everything (events, visibility, and cleanup)
  useEffect(() => {
    if (tooltipText === null || !anchorRef.current) {
      return;
    }

    const anchorElement = anchorRef.current;
    const popover = popoverRef.current;

    // Handler for mouse entering element
    const handleEnter = () => {
      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);

        hideTimeoutRef.current = null;
      }

      setIsVisible(true);
    };

    // Handler for mouse leaving element
    const handleLeave = () => {
      hideTimeoutRef.current = window.setTimeout(() => {
        setIsVisible(false);

        hideTimeoutRef.current = null;
      }, 100);
    };

    // Attach event listeners if in hover mode
    if (visible === 'hover') {
      anchorElement.addEventListener('mouseenter', handleEnter);

      anchorElement.addEventListener('mouseleave', handleLeave);
    }

    // Control the popover visibility
    if (popover) {
      if (shouldShow) {
        popover.opener = anchorElement;

        popover.open = true;
      } else {
        popover.open = false;
      }
    }

    // Cleanup function
    return () => {
      if (visible === 'hover') {
        anchorElement.removeEventListener('mouseenter', handleEnter);

        anchorElement.removeEventListener('mouseleave', handleLeave);
      }

      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);
      }

      if (popover) {
        popover.open = false;
      }
    };
  }, [tooltipText, shouldShow, visible]);

  // Create tooltip content with mouse handlers
  const tooltipContent = useMemo(() => {
    if (tooltipText === null) {
      return null;
    }

    // Mouse handlers for tooltip content - for hovering over the tooltip itself
    const handleMouseEnter =
      visible === 'hover'
        ? () => {
            if (hideTimeoutRef.current !== null) {
              window.clearTimeout(hideTimeoutRef.current);

              hideTimeoutRef.current = null;
            }

            setIsVisible(true);
          }
        : undefined;

    const handleMouseLeave =
      visible === 'hover'
        ? () => {
            hideTimeoutRef.current = window.setTimeout(() => {
              setIsVisible(false);

              hideTimeoutRef.current = null;
            }, 100);
          }
        : undefined;

    const contentProps = {
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
    };

    // Render string content as paragraphs
    if (typeof tooltipText === 'string') {
      const paragraphs = tooltipText.split(/\n{2,}/g);
      const isSingleLine = tooltipText.indexOf('\n') === -1 && paragraphs.length === 1;

      return (
        <div {...contentProps}>
          {paragraphs.map((text, idx) => (
            <p
              className={classNames(styles['ui5-tooltip-text'], {
                [styles['ui5-tooltip-text-centered']]: isSingleLine,
              })}
              key={idx}
            >
              {text}
            </p>
          ))}
        </div>
      );
    }

    // Otherwise render React component
    return <div {...contentProps}>{tooltipText}</div>;
  }, [tooltipText, visible, hideTimeoutRef]);

  // This sets an initial position for the popover to prevent layout shifts
  // when the UI5 positioning system takes control of placement
  const initialPositioningStyle = shouldShow ? { top: '0', left: 0 } : {};

  return (
    <>
      {anchor}
      {tooltipText !== null && (
        <Popover
          accessibleRole="Dialog"
          className={classNames(styles['ui5-tooltip'], {
            [styles['visible']]: shouldShow,
          })}
          hideArrow={false}
          horizontalAlign={'Center'}
          modal={false}
          placement={placement}
          ref={popoverRef}
          style={initialPositioningStyle}
          verticalAlign={'Center'}
        >
          {tooltipContent}

          <div className={styles['ui5-tooltip-additional-text-wrapper']}>
            {additionalText && (
              <span className={styles['ui5-tooltip-additional-text']}>
                {additionalText}
              </span>
            )}
          </div>
        </Popover>
      )}
    </>
  );
}
